import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Section,
  Container,
  Heading,
  Card,
  CardInner,
  Button,
  Input,
} from "toddlar-ui";
import { useRegister } from "../state/register";
import { apiService, type RegisterData } from "../services/api";
import { useAuth } from "../state/auth";

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { billingPeriod, selectedPlan, clear } = useRegister();
  const { login } = useAuth();

  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const price = selectedPlan ? selectedPlan.prices[billingPeriod] : 0;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPlan) {
      navigate("/register");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const payload: RegisterData = {
        email: form.email,
        password: form.password,
      };

      const res = await apiService.register(payload);
      if (res.success && res.data) {
        login(res.data.user, res.data.token);
        // Immediately create subscription checkout and redirect
        try {
          // Include selected plan context for backend to persist
          const checkoutRes = await fetch(
            `${
              process.env.REACT_APP_API_URL || "http://localhost:5000/api"
            }/billing/create-checkout-session`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${encodeURIComponent(res.data.token)}`,
              },
              body: JSON.stringify({
                planId: selectedPlan.id,
                pointsPerCycle: selectedPlan.points,
                billingPeriod,
              }),
            }
          );
          const checkout = await checkoutRes.json();
          if (checkout?.success && checkout?.data?.url) {
            window.location.href = checkout.data.url;
            return;
          }
        } catch (e) {
          // Fall back to welcome if checkout creation fails
        }
        clear();
        navigate("/welcome");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand-50">
      <Section>
        <Container>
          <Heading size="h2" className="text-ink mb-4">
            Create your account
          </Heading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardInner>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm text-ink mb-1">Email</label>
                    <Input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-ink mb-1">
                      Password
                    </label>
                    <Input
                      type="password"
                      name="password"
                      value={form.password}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  {error ? (
                    <div className="text-red-600 text-sm">{error}</div>
                  ) : null}
                  <Button type="submit" disabled={loading}>
                    {loading ? "Creating account..." : "Create account"}
                  </Button>
                </form>
              </CardInner>
            </Card>

            <Card>
              <CardInner>
                <Heading size="h3" className="text-ink mb-2">
                  Your selection
                </Heading>
                {selectedPlan ? (
                  <div className="space-y-2 text-ink">
                    <div className="flex justify-between">
                      <span>Plan</span>
                      <span>{selectedPlan.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Points</span>
                      <span>{selectedPlan.points}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Billing</span>
                      <span>{billingPeriod}</span>
                    </div>
                    <div className="flex justify-between font-semibold">
                      <span>Total</span>
                      <span>{price}kr</span>
                    </div>
                    <Button
                      variant="secondary"
                      onClick={() => navigate("/register")}
                    >
                      Change selection
                    </Button>
                  </div>
                ) : (
                  <div className="text-ink">
                    No plan selected.{" "}
                    <button
                      className="text-brand-600 underline"
                      onClick={() => navigate("/register")}
                    >
                      Choose a plan
                    </button>
                  </div>
                )}
              </CardInner>
            </Card>
          </div>
        </Container>
      </Section>
    </div>
  );
};

export default Signup;
